import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { NavigationSection, BookingStatus } from "@repo/types";
import { Button, cn } from "@repo/ui-web";
import { MainMenu, type MainMenuVariant } from "./main-menu";
import { fetchBookings, fetchCustomers, fetchStaffs, fetchSystemSettings, type Booking } from "@repo/api-client";
import { bookingStatusLabels, qk, useDashboardBookingActions } from "@repo/lib";
import { Bot, Menu } from "lucide-react";

// 右下角（底部置中）AI 助理浮動按鈕；.env 設 VITE_ENABLE_AI_ASSISTANT=false 可隱藏（未設定＝顯示）
const SHOW_AI_ASSISTANT = import.meta.env.VITE_ENABLE_AI_ASSISTANT !== "false";

const paymentOptionCatalog: Record<string, { value: string; label: string }> = {
  "": { value: "", label: "未指定" },
  cash: { value: "cash", label: "現金" },
  wallet: { value: "wallet", label: "儲值金" },
  bank_transfer: { value: "bank_transfer", label: "銀行轉帳" },
  line_pay: { value: "line_pay", label: "LINE Pay" },
  credit_card: { value: "credit_card", label: "刷卡" },
  other: { value: "other", label: "其他" }
};

export interface WorkspaceLayoutProps {
  userName?: string;
  userStaffId?: string;
  onLogout?: () => void;
  sections: NavigationSection[];
  menuVariant?: MainMenuVariant;
  userRole?: string;
  children: React.ReactNode;
}

export const WorkspaceLayout: React.FC<WorkspaceLayoutProps> = ({
  userName,
  userStaffId,
  onLogout,
  sections,
  menuVariant = "default",
  userRole,
  children
}) => {
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [todayPanelOpen, setTodayPanelOpen] = React.useState(false);
  const [todayActionId, setTodayActionId] = React.useState<string | null>(null);
  const [todayOptimistic, setTodayOptimistic] = React.useState<Record<string, BookingStatus>>({});
  const [todayPaymentDialogOpen, setTodayPaymentDialogOpen] = React.useState(false);
  const [todayPaymentBookingId, setTodayPaymentBookingId] = React.useState<string | null>(null);
  const [todayPaymentMethod, setTodayPaymentMethod] = React.useState("");
  const queryClient = useQueryClient();
  const todayDateKey = React.useMemo(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  }, []);

  const {
    data: todayBookings = [],
    isFetching: todayLoading,
  } = useQuery<Booking[]>({
    queryKey: qk.dashboardBookings(todayDateKey),
    queryFn: () => fetchBookings({ date: todayDateKey, limit: 200 }),
    enabled: todayPanelOpen,
  });

  const { data: todayCustomers = [] } = useQuery({
    queryKey: qk.customers(),
    queryFn: () => fetchCustomers(),
    enabled: todayPanelOpen,
  });

  const { data: todayStaffs = [] } = useQuery({
    queryKey: qk.staffs(),
    queryFn: () => fetchStaffs(),
    enabled: todayPanelOpen,
  });

  const { data: todaySystemSettings = null } = useQuery<Record<string, any> | null>({
    queryKey: qk.systemSettings(),
    queryFn: () => fetchSystemSettings().catch(() => null),
    enabled: todayPanelOpen,
  });

  const todayPaymentOptions = React.useMemo(() => {
    const list = [paymentOptionCatalog[""]];
    const cashEnabled = todaySystemSettings?.enablePaymentCash !== false;
    const bankEnabled = Boolean(todaySystemSettings?.enablePaymentBankTransfer);
    const lineEnabled = Boolean(todaySystemSettings?.enablePaymentLinepay);
    if (cashEnabled) list.push(paymentOptionCatalog.cash);
    if (lineEnabled) list.push(paymentOptionCatalog.line_pay);
    if (bankEnabled) list.push(paymentOptionCatalog.bank_transfer);
    return list.length > 0 ? list : [paymentOptionCatalog[""]];
  }, [todaySystemSettings]);

  React.useEffect(() => {
    setIsCollapsed(pathname?.startsWith("/bookings") ?? false);
  }, [pathname]);

  React.useEffect(() => {
    if (todayPanelOpen) {
      setTodayOptimistic({});
    }
  }, [todayPanelOpen]);

  const { updateStatus } = useDashboardBookingActions<{
    id: string;
    depositPlan: { suggestedAmount: number };
  }>({
    selectedDate: todayDateKey,
  });

  const handleTodayStatusChange = React.useCallback(
    async (bookingId: string, newStatus: BookingStatus, paymentMethod?: string) => {
      setTodayActionId(bookingId);
      setTodayOptimistic((prev) => ({ ...prev, [bookingId]: newStatus }));
      try {
        await queryClient.invalidateQueries({ queryKey: qk.bookings() });
        await updateStatus(bookingId, newStatus, paymentMethod);
      } catch {
        setTodayOptimistic((prev) => {
          const next = { ...prev };
          delete next[bookingId];
          return next;
        });
      } finally {
        setTodayActionId(null);
      }
    },
    [queryClient, updateStatus]
  );

  const todayCustomerName = React.useCallback(
    (id: string) => {
      const c = todayCustomers.find((c) => c.id === id);
      return c?.name ?? "-";
    },
    [todayCustomers]
  );

  const todayStaffName = React.useCallback(
    (id: string) => {
      const s = todayStaffs.find((s) => s.id === id);
      return (s as any)?.display_name || s?.name || "-";
    },
    [todayStaffs]
  );

  const isAiChat = pathname === "/ai-chat";

  return (
    <div className="flex h-screen overflow-hidden bg-gradient-to-br from-brand-25 via-brand-50/70 to-brand-100/80 text-brand-800">
      <div className={isAiChat ? "hidden" : "relative flex-shrink-0 p-0 lg:px-4 lg:py-4"}>
        <MainMenu
          sections={sections}
          isCollapsed={isCollapsed}
          onToggleCollapsed={() => setIsCollapsed((prev) => !prev)}
          isMobileOpen={mobileOpen}
          onCloseMobile={() => setMobileOpen(false)}
          variant={menuVariant}
          userRole={userRole}
          userName={userName}
          userStaffId={userStaffId}
          onLogout={onLogout}
        />
      </div>

      <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="fixed left-3 top-3 z-30 inline-flex items-center gap-1.5 rounded-full border border-brand-100/60 bg-white/90 px-3 py-2 text-sm font-medium text-brand-700 shadow-md shadow-brand-900/10 backdrop-blur transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60 lg:hidden"
          aria-label="開啟主選單"
        >
          <Menu className="h-4 w-4" strokeWidth={2} />
          <span>選單</span>
        </button>

        <main className={isAiChat ? "flex-1 overflow-hidden" : "flex-1 px-5 pb-8 pt-16 sm:px-8 lg:px-4 lg:py-8"}>{children}</main>

        {todayPanelOpen ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div
              className="absolute inset-0 bg-black/30 backdrop-blur-sm"
              onClick={() => setTodayPanelOpen(false)}
            />
            <div className="relative z-10 w-full max-w-lg rounded-3xl border border-brand-100 bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-brand-100 px-5 py-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-500">今日預約</p>
                  <h3 className="text-lg font-semibold text-brand-800">
                    {new Intl.DateTimeFormat("zh-TW", { year: "numeric", month: "2-digit", day: "2-digit", weekday: "long" }).format(new Date())}
                  </h3>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setTodayPanelOpen(false)}>
                  ✕
                </Button>
              </div>
              <div className="max-h-[60vh] overflow-y-auto px-5 py-4 space-y-3">
                {todayLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-r-transparent" />
                  </div>
                ) : todayBookings.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-brand-200 bg-brand-50 px-4 py-6 text-center text-sm text-brand-500">
                    今日無預約
                  </div>
                ) : (
                  todayBookings
                    .filter((bk) => bk.booking_date === todayDateKey)
                    .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""))
                    .map((bk) => {
                    const currentStatus = (todayOptimistic[bk.id] ?? bk.status) as BookingStatus;
                    const isUpdating = todayActionId === bk.id;
                    const startTime = bk.start_time?.slice(0, 5) || "--:--";
                    const endTime = bk.end_time?.slice(0, 5) || "--:--";
                    return (
                      <div key={bk.id} className="rounded-xl border border-brand-100 bg-white p-3 shadow-sm space-y-2">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-brand-800">
                              {startTime} – {endTime}
                            </div>
                            <div className="mt-0.5 text-sm text-brand-700">
                              {bk.customer_name || todayCustomerName(bk.customer_id) || bk.attendee_name || "未指定"}
                            </div>
                            <div className="text-xs text-brand-500">
                              {todayStaffName(bk.staff_id)}
                              {bk.service_name ? ` · ${bk.service_name}` : ""}
                            </div>
                          </div>
                          <a
                            href={`/bookings/${bk.id}`}
                            className="shrink-0 rounded-lg px-2 py-1 text-xs text-brand-500 hover:bg-brand-50 hover:text-brand-700 transition"
                          >
                            編輯
                          </a>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {(["pending", "checked_in", "paid", "cancelled", "no_show"] as BookingStatus[]).map((s) => (
                            <button
                              key={s}
                              type="button"
                              disabled={isUpdating}
                              onClick={() => {
                                if (currentStatus === s) return;
                                if (s === "paid" || s === "completed") {
                                  setTodayPaymentBookingId(bk.id);
                                  const existing = (bk as any).deposit_payment_method || "";
                                  const allowed = todayPaymentOptions.map((o) => o.value);
                                  const next = (existing && allowed.includes(existing)) ? existing : (allowed[0] || "");
                                  setTodayPaymentMethod(next);
                                  setTodayPaymentDialogOpen(true);
                                  return;
                                }
                                handleTodayStatusChange(bk.id, s);
                              }}
                              className={cn(
                                "rounded-full px-2.5 py-0.5 text-xs font-medium transition",
                                currentStatus === s
                                  ? s === "pending" ? "bg-[#C9AD94] text-white"
                                  : s === "checked_in" ? "bg-[#7EA886] text-white"
                                  : s === "paid" ? "bg-[#6AABC9] text-white"
                                  : s === "cancelled" ? "bg-rose-500 text-white"
                                  : "bg-[#85858F] text-white"
                                  : s === "pending" ? "border border-[#D9C4AE] text-[#8B7355] hover:bg-[#F7F0E8]"
                                  : s === "checked_in" ? "border border-[#9ABD9F] text-[#4A6B50] hover:bg-[#EDF3EE]"
                                  : s === "paid" ? "border border-[#88BFDB] text-[#2E6585] hover:bg-[#EEF6FB]"
                                  : s === "cancelled" ? "border border-rose-300 text-rose-700 hover:bg-rose-50"
                                  : "border border-[#9E9EA6] text-[#55555E] hover:bg-[#F0F0F2]",
                                isUpdating && "opacity-50 cursor-not-allowed"
                              )}
                            >
                              {bookingStatusLabels[s]}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                    })
                )}
              </div>
            </div>
          </div>
        ) : null}
        {todayPaymentDialogOpen ? (
          <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
            <div
              className="absolute inset-0 bg-black/30 backdrop-blur-sm"
              onClick={() => setTodayPaymentDialogOpen(false)}
            />
            <div className="relative z-10 w-full max-w-sm rounded-2xl border border-brand-100 bg-white p-5 shadow-2xl">
              <h3 className="text-base font-semibold text-brand-800">選擇結帳方式</h3>
              <p className="mt-1 text-xs text-brand-500">狀態設為「已結帳」時，請記錄收款方式。</p>
              <div className="mt-3">
                <label className="text-xs text-brand-500">結帳方式</label>
                <select
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-300"
                  value={todayPaymentMethod}
                  onChange={(e) => setTodayPaymentMethod(e.target.value)}
                >
                  {todayPaymentOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="secondary" size="sm" onClick={() => setTodayPaymentDialogOpen(false)}>
                  取消
                </Button>
                <Button
                  size="sm"
                  disabled={!!todayActionId}
                  onClick={() => {
                    if (!todayPaymentBookingId) return;
                    setTodayPaymentDialogOpen(false);
                    handleTodayStatusChange(todayPaymentBookingId, "paid", todayPaymentMethod);
                  }}
                >
                  {todayActionId ? "儲存中..." : "確認並儲存"}
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {!isAiChat && SHOW_AI_ASSISTANT && (
        <div className="pointer-events-none fixed bottom-8 left-1/2 z-40 -translate-x-1/2">
          <div className="relative flex h-12 w-12 items-center justify-center">
            <motion.div
              className="absolute inset-0 rounded-full bg-brand-500/40"
              animate={{ scale: [1, 1.7, 1], opacity: [0.5, 0, 0.5] }}
              transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
            />
            <button aria-label="AI 助理"
              type="button"
              onClick={() => navigate("/ai-chat")}
              className="pointer-events-auto relative flex h-12 w-12 items-center justify-center rounded-full bg-brand-700 text-white shadow-lg shadow-brand-700/30 transition hover:bg-brand-800 active:scale-95"
              title="AI 助理"
            >
              <Bot className="h-5 w-5" strokeWidth={1.8} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
